import { Component, Vue } from 'vue-property-decorator';
import lodash from 'lodash';

import { ALL_TIME_VALUE, CONTRIBUTION_LEVELS, NOTIFICATION } from '@/constants';
import {
  extractQuarterFromName,
  getContributionLevelTextFromDrops,
  sumByField,
  tableHeight,
  toTreeOptionsByQuarterOptions
} from '@/helpers';

import {
  ContributionLevel,
  TableHeaderColumns,
  UserContributionLevelPeriodDTO,
  UserContributionLevelDisplayDTO,
  ProjectInformationDTO,
  StatisticUserProject,
  SelectionOptions,
  AssessmentPeriodDTO,
  TreeOption
} from '@/models';
import {
  fetchAssessmentPeriods,
  fetchContributionLevels,
  fetchPeriodsContributionLevels,
  fetchProjectsOfCurrentUser
} from '@/services';

import ExportByUser from '@/services/export-by-user';
import ContributionLevelIcon from '@/components/common/contribution-level-icon/ContributionLevelIcon.vue';
import FilterDropdown from '@/components/common/filter-dropdown/FilterDropdown.vue';
import Pagination from '@/components/common/pagination/Pagination.vue';
import SearchInput from '@/components/common/search-input/SearchInput.vue';
import ContributorsExport from '@/services/contributors-export';
import TreeFilterDialog from '@/components/common/tree-filter-dialog/TreeFilterDialog.vue';

@Component({
  components: {
    ContributionLevelIcon,
    FilterDropdown,
    SearchInput,
    Pagination,
    TreeFilterDialog
  }
})
export default class UserStatistics extends Vue {
  private periodsContributions: UserContributionLevelPeriodDTO[] = [];
  private readonly headerTable: TableHeaderColumns = [
    {
      value: 'order',
      text: '#'
    },
    {
      value: 'id',
      text: 'ID'
    },
    {
      value: 'name',
      text: 'Employee Name',
      cellClass: 'user-statistics__cell-user'
    },
    {
      value: 'project',
      text: 'Project(s)'
    },
    {
      value: 'drops',
      text: 'Accumulated Pollens',
      align: 'end'
    },
    {
      text: 'Current Level',
      value: 'currentLevel'
    }
  ];
  private periodOptions: SelectionOptions = [];
  private selectedPeriods = [] as string[];
  private projectOptions: SelectionOptions = [];
  private selectedProjects = [] as string[];
  private searchUser = '';
  private allMembers: UserContributionLevelDisplayDTO[] = [];
  private statisticUserProjects: StatisticUserProject[] = [];
  private selectedRows: UserContributionLevelDisplayDTO[] = [];
  private singleSelect = false;
  private loading = false;
  private allAssessmentPeriods: AssessmentPeriodDTO[] = [];

  private pageCount = 0;
  private page = 1;
  private itemsPerPage = 10;
  private isExporting = false;
  private modalShown = false;

  get tableHeight(): string {
    return tableHeight(this.pageCount);
  }

  get displayDataUserContributionLevel(): UserContributionLevelDisplayDTO[] {
    this.allMembers = this.buildDisplayPeriodsAndProjectsContributionLevels();
    return this.allMembers;
  }

  get textExportButton(): string {
    if (this.selectedRows.length === this.allMembers.length || this.selectedRows.length === 0) {
      return 'Export all';
    }
    return this.selectedRows.length !== 1 ? `Export ${this.selectedRows.length} users` : 'Export 1 user';
  }

  async created(): Promise<void> {
    this.loading = true;

    //setInitialStatisticUserProjects
    const { data: allMembersData } = await fetchContributionLevels();
    const { data: projects } = await fetchProjectsOfCurrentUser();
    allMembersData.forEach(member => {
      const projectByUserId = projects.filter(project => {
        const projectAssessments = project.projectAssessments;
        const projectAssessmentsByUserId = projectAssessments.filter(projectAssessment => {
          const userEngagementsByUserId = projectAssessment.userEngagements.filter(
            userEngagement => userEngagement.user.id === member.user.id
          );
          return userEngagementsByUserId.length > 0;
        });
        return projectAssessmentsByUserId.length > 0;
      });
      const projectInformation: ProjectInformationDTO[] = [];
      projectByUserId.forEach(project => {
        project.projectAssessments.forEach(projectAssessment => {
          if (projectAssessment.userEngagements.find(userEngagement => userEngagement.user.id === member.user.id)) {
            projectInformation.push({
              id: project.id,
              projectName: project.name,
              assessmentPeriodId: projectAssessment.assessmentPeriodId
            });
          }
        });
      });
      this.statisticUserProjects.push({
        userId: member.user.id,
        projectInformation
      });
    });

    //setInitialPeriodsContributions
    const { data: periodsContributionsData } = await fetchPeriodsContributionLevels();
    this.periodsContributions = periodsContributionsData;

    //setInitialPeriodsOptions
    const { data: allAssessmentPeriods } = await fetchAssessmentPeriods();
    const sortedPeriods = allAssessmentPeriods.sort((a, b) => (a.createdAt < b.createdAt ? -1 : 1));
    this.allAssessmentPeriods = sortedPeriods;

    sortedPeriods.forEach(item => {
      this.periodOptions.push({ text: item.name, value: item.id?.toString() || '-1' });
    });

    //setInitialProjectsOptions
    projects.forEach(project => {
      this.projectOptions.push({ text: project.name, value: project.id.toString() });
    });

    this.loading = false;
  }

  getContributionLevelFromText(contributionLevelText: string): ContributionLevel {
    return CONTRIBUTION_LEVELS[contributionLevelText];
  }

  buildDisplayPeriodsAndProjectsContributionLevels(): UserContributionLevelDisplayDTO[] {
    const isFilterByPeriods = this.selectedPeriods.length > 0;
    const isFilterByProjects = this.selectedProjects.length > 0;
    const periodsContributionsSelected = isFilterByPeriods
      ? this.periodsContributions.filter(pc =>
          this.selectedPeriods.includes(pc.assessmentPeriod?.id?.toString() || '-1')
        )
      : this.periodsContributions;
    const userContributions = lodash.uniqBy(periodsContributionsSelected, 'user.id').map(pc => {
      const userAccumulatedDrops = sumByField(
        periodsContributionsSelected.filter(p => p.user.id === pc.user.id),
        'periodAccumulatedDrops'
      );
      const projectsByUserId =
        this.statisticUserProjects.find(project => project.userId === pc.user.id) || ({} as StatisticUserProject);

      const projectInformation = isFilterByPeriods
        ? projectsByUserId.projectInformation.filter(pi =>
            this.selectedPeriods.includes(pi.assessmentPeriodId.toString())
          )
        : projectsByUserId.projectInformation;

      return {
        id: parseInt(pc.user.code),
        name: pc.user.fullName,
        project: projectInformation,
        drops: userAccumulatedDrops,
        currentLevel: getContributionLevelTextFromDrops(userAccumulatedDrops)
      };
    });

    const filteredUserContributions = isFilterByProjects
      ? userContributions.filter(uc => uc.project.some(p => this.selectedProjects.includes(p.id.toString())))
      : userContributions;

    return filteredUserContributions.map((uc, index) => ({ ...uc, order: index + 1 }));
  }

  displayProject(projects: ProjectInformationDTO[]): string {
    return (
      lodash
        .uniqBy(projects, 'id')
        ?.map(project => project.projectName)
        .join(', ') || ''
    );
  }

  onPeriodChange(selectedPeriods: string[]): void {
    this.selectedPeriods = selectedPeriods;
    this.selectedRows = [];
  }

  onProjectChange(selectedProjects: string[]): void {
    this.selectedProjects = selectedProjects;
    this.selectedRows = [];
  }

  onSearch(searchText: string): void {
    this.searchUser = searchText;
  }

  async exportUser(): Promise<void> {
    const assessmentPeriods = this.getUserPeriodsInformation();
    const userToExport = this.selectedRows.length ? this.selectedRows : this.allMembers;
    const assessmentPeriodsExport = new ExportByUser(assessmentPeriods, userToExport);
    await assessmentPeriodsExport.generateXLSXFile();
  }

  getUserPeriodsInformation(): AssessmentPeriodDTO[] {
    if (this.selectedPeriods.length) {
      return this.allAssessmentPeriods.filter(period => this.selectedPeriods.includes(period.id?.toString() || '-1'));
    }
    return this.allAssessmentPeriods;
  }

  onPageChange(page: number): void {
    this.page = page;
  }

  onItemsPerPageChange(itemsPerPage: number): void {
    this.itemsPerPage = itemsPerPage;
  }

  selectAll(): void {
    this.selectedRows =
      this.selectedRows.length === this.displayDataUserContributionLevel.length
        ? []
        : this.displayDataUserContributionLevel;
  }

  get contributorsOptions(): TreeOption[] {
    const quarterOptions = this.periodOptions.map(p => ({
      text: extractQuarterFromName(p.text),
      value: p.value
    }));
    return toTreeOptionsByQuarterOptions(quarterOptions);
  }

  onModalCancel(): void {
    this.modalShown = false;
  }

  onModalConfirm(selecteds: string[]): void {
    let finalSelected = selecteds.map(Number);
    const isAllTimeSelected = selecteds.indexOf(ALL_TIME_VALUE.toString()) > -1;
    if (isAllTimeSelected) {
      finalSelected = this.periodOptions
        .map(opt => opt.value)
        .map(Number)
        .concat(finalSelected);
    }
    this.isExporting = true;
    const contributorsExport = new ContributorsExport(selecteds.map(Number), finalSelected);
    contributorsExport.generateXLSXFile().finally(() => {
      this.isExporting = false;
      const notificationInfo = {
        showing: true,
        type: NOTIFICATION.TYPE.success,
        title: NOTIFICATION.TEXT.exportDataSuccess,
        content: [],
        icon: NOTIFICATION.ICON.success,
        multiline: false,
        height: 0
      };
      this.$store.commit('notification/setNotification', notificationInfo);
    });
  }
}
